import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { toast } from 'react-toastify';

// Interface para os dados da etiqueta
export interface Etiqueta {
  id: string;
  data: string;
  id_usuario_entrada: string;
  nome_usuario_entrada: string;
  id_usuario_saida?: string;
  nome_usuario_saida?: string;
  quantidade: number;
  observacao?: string;
  created_at?: string;
}

// Interface para os dados de entrada da etiqueta
export interface EtiquetaInput {
  data: string;
  id_usuario_entrada: string;
  nome_usuario_entrada: string;
  id_usuario_saida?: string;
  nome_usuario_saida?: string;
  quantidade: number;
  observacao?: string;
}

export function useEtiquetas() {
  const queryClient = useQueryClient();

  // Query para listar etiquetas
  const { data: etiquetas, isLoading: listLoading } = useQuery({
    queryKey: ['etiquetas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('etiquetas')
        .select('*')
        .order('data', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Query para buscar etiqueta específica
  const useEtiqueta = (id: string) => {
    return useQuery({
      queryKey: ['etiquetas', id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('etiquetas')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        return data;
      },
      enabled: !!id,
    });
  };

  // Mutation para criar etiqueta
  const { mutateAsync: criarEtiqueta } = useMutation({
    mutationFn: async (etiqueta: EtiquetaInput) => {
      const { error } = await supabase
        .from('etiquetas')
        .insert([etiqueta]);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['etiquetas'] });
      toast.success('Etiqueta registrada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao registrar etiqueta: ' + error.message);
    },
  });

  // Mutation para atualizar etiqueta
  const { mutateAsync: atualizarEtiqueta } = useMutation({
    mutationFn: async ({ id, etiqueta }: { id: string; etiqueta: Partial<EtiquetaInput> }) => {
      const { error } = await supabase
        .from('etiquetas')
        .update(etiqueta)
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['etiquetas'] });
      queryClient.invalidateQueries({ queryKey: ['etiquetas', variables.id] });
      toast.success('Etiqueta atualizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao atualizar etiqueta: ' + error.message);
    },
  });

  // Mutation para excluir etiqueta
  const { mutateAsync: excluirEtiqueta } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('etiquetas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['etiquetas'] });
      queryClient.invalidateQueries({ queryKey: ['etiquetas', id] });
      toast.success('Etiqueta excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao excluir etiqueta: ' + error.message);
    },
  });

  // Query para buscar etiquetas por usuário de entrada
  const useEtiquetasPorUsuarioEntrada = (idUsuario: string) => {
    return useQuery({
      queryKey: ['etiquetas', 'entrada', idUsuario],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('etiquetas')
          .select('*')
          .eq('id_usuario_entrada', idUsuario)
          .order('data', { ascending: false });

        if (error) throw error;
        return data || [];
      },
      enabled: !!idUsuario,
    });
  };

  // Query para buscar etiquetas por usuário de saída
  const useEtiquetasPorUsuarioSaida = (idUsuario: string) => {
    return useQuery({
      queryKey: ['etiquetas', 'saida', idUsuario],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('etiquetas')
          .select('*')
          .eq('id_usuario_saida', idUsuario)
          .order('data', { ascending: false });

        if (error) throw error;
        return data || [];
      },
      enabled: !!idUsuario,
    });
  };

  return {
    etiquetas,
    listLoading,
    useEtiqueta,
    criarEtiqueta,
    atualizarEtiqueta,
    excluirEtiqueta,
    useEtiquetasPorUsuarioEntrada,
    useEtiquetasPorUsuarioSaida,
  };
} 